// Descrição da tarefa na lista e no cartão "Em foco" (US-098): recolhida numa linha com "…";
// um clique abre por inteiro (até 6 linhas, com rolagem — AC-226) e outro clique, um clique em
// qualquer outro lugar do app ou Esc fecham. Só uma fica aberta por vez (AC-225).
import { useEffect, useRef, useSyncExternalStore, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { cls } from './NewTaskForm';

// qual descrição está aberta (uma só no app inteiro)
let openKey: string | null = null;
const listeners = new Set<() => void>();
function setOpenKey(key: string | null) {
  if (key === openKey) return;
  openKey = key;
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/**
 * Recolhida, mostra só a primeira linha escrita (as quebras do usuário valem); se houver mais
 * linhas, termina com "…" para indicar que tem mais.
 */
export function firstLine(text: string): string {
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const i = lines.findIndex(Boolean);
  if (i < 0) return '';
  return lines.slice(i + 1).some(Boolean) ? `${lines[i]} …` : lines[i];
}

/** `place` separa a mesma tarefa na lista e no "Em foco" */
export function TaskDescription({ taskId, text, place }: { taskId: string; text: string; place: 'list' | 'focus' }) {
  const { t } = useTranslation();
  const key = `${place}:${taskId}`;
  const open = useSyncExternalStore(subscribe, () => openKey === key, () => false);
  const ref = useRef<HTMLDivElement>(null);

  // aberta: qualquer clique fora dela, ou Esc, fecha
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenKey(null);
    };
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && setOpenKey(null);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  // a tarefa saiu da tela (concluída, excluída): não deixa a descrição marcada como aberta
  useEffect(() => () => void (openKey === key && setOpenKey(null)), [key]);

  const toggle = () => setOpenKey(open ? null : key);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    // a linha da lista também ouve o teclado (arrastar); a tecla fica só com a descrição
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-label={t('task.description')}
      className={cls('task-desc', open && 'task-desc--open')}
      data-testid="task-description"
      onClick={toggle}
      onKeyDown={onKey}
    >
      <Icon name="fileText" size={13} className="task-desc__icon" />
      <span className="task-desc__text">{open ? text : firstLine(text)}</span>
    </div>
  );
}
